import React, { useState, useEffect, useRef } from "react";
import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";
import "./App.css";

export default function App() {
  // === State and Refs ===
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");
  const [vsComputer, setVsComputer] = useState(true);
  const [difficulty, setDifficulty] = useState("medium");
  const skillMap = { easy: 1, medium: 10, hard: 20 };

  const [game, setGame] = useState(new Chess());
  const gameRef = useRef(game);
  const [position, setPosition] = useState("start");
  const [history, setHistory] = useState([]);

  const stockfishRef = useRef(null);

  const [turn, setTurn] = useState("w");
  const [playerColor, setPlayerColor] = useState("w");

  const [timerActive, setTimerActive] = useState(false);
  const timerRef = useRef(null);

  const [timeControl, setTimeControl] = useState(300); // 5 minutes default
  const [whiteTime, setWhiteTime] = useState(300);
  const [blackTime, setBlackTime] = useState(300);

  // === Theme & Mode ===
  useEffect(() => {
    document.body.className = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => (prev === "light" ? "dark" : "light"));

  // === Stockfish Setup ===
  useEffect(() => {
    const stockfish = new Worker("stockfish.js");
    stockfishRef.current = stockfish;

    stockfish.onmessage = (event) => {
      const msg = event.data;
      if (msg.startsWith("bestmove")) {
        const move = msg.split(" ")[1];
        if (move) {
          gameRef.current.move({
            from: move.substring(0, 2),
            to: move.substring(2, 4),
            promotion: "q",
          });

          setGame(new Chess(gameRef.current.fen()));
          setPosition(gameRef.current.fen());
          setHistory(gameRef.current.history({ verbose: true }));
          setTurn(gameRef.current.turn());

        }
      }
    };

    stockfish.postMessage("uci");
    stockfish.postMessage("isready");

    return () => stockfish.terminate();
  }, []);

  function requestAIMove() {
    const fen = gameRef.current.fen();
    stockfishRef.current.postMessage(`position fen ${fen}`);
    stockfishRef.current.postMessage(`setoption name Skill Level value ${skillMap[difficulty]}`);
    stockfishRef.current.postMessage("go depth 15");
  }

  // === Move Logic ===
  // === Move Logic ===
function onDrop(sourceSquare, targetSquare) {
  const move = {
    from: sourceSquare,
    to: targetSquare,
    promotion: "q",
  };

  try {
    const result = gameRef.current.move(move);

    if (!result) {
      alert("Invalid move! Try again."); // 🔔 This should now trigger
      return false;
    }

    // Valid move updates
    setGame(new Chess(gameRef.current.fen()));
    setPosition(gameRef.current.fen());
    setHistory(gameRef.current.history({ verbose: true }));
    setTurn(gameRef.current.turn());

    if (!timerActive) setTimerActive(true);

    // Let the computer respond
    if (
      vsComputer &&
      gameRef.current.turn() !== playerColor &&
      !gameRef.current.isGameOver()
    ) {
      setTimeout(() => requestAIMove(), 400);
    }

    return true;

  } catch (error) {
    console.error("Move failed:", error);
    alert("Something went wrong. Try again."); // fallback
    return false;
  }
}




  // === Game Control ===
  function newGame(selectedColor = playerColor) {
    const newChess = new Chess();
    gameRef.current = newChess;
    setGame(newChess);
    setPosition("start");
    setHistory([]);
    setWhiteTime(timeControl);
    setBlackTime(timeControl);
    setTurn("w");
    setPlayerColor(selectedColor);
    setTimerActive(false);

    // If computer is black and goes first
    if (vsComputer && selectedColor === "b") {
      setTimeout(() => {
        requestAIMove(); // white starts
      }, 400);
    }

  }


  function undoMove() {
    gameRef.current.undo();
    setGame(new Chess(gameRef.current.fen()));
    setPosition(gameRef.current.fen());
    setHistory(gameRef.current.history({ verbose: true }));
    setTurn(gameRef.current.turn());
  }

  function downloadPGN() {
    const pgn = gameRef.current.pgn();
    const blob = new Blob([pgn], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "game.pgn";
    a.click();
    URL.revokeObjectURL(url);
  }

  // === Timers ===
  useEffect(() => {
    if (!timerActive) return;

    timerRef.current = setInterval(() => {
      if (turn === "w") {
        setWhiteTime((prev) => Math.max(prev - 1, 0));
      } else {
        setBlackTime((prev) => Math.max(prev - 1, 0));
      }
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [timerActive, turn]);

  useEffect(() => {
    if (
      whiteTime === 0 ||
      blackTime === 0 ||
      gameRef.current.isGameOver()
    ) {
      setTimerActive(false);
    }
  }, [whiteTime, blackTime, position]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  // === Move History ===
  const renderHistory = () => {
    const rows = [];

    for (let i = 0; i < history.length; i += 2) {
      const whiteMove = history[i]?.san || "";
      const blackMove = history[i + 1]?.san || "";

      rows.push(
        <tr key={i}>
          <td>{i / 2 + 1}.</td>
          <td>{whiteMove}</td>
          <td>{blackMove}</td>
        </tr>
      );
    }

    return rows;
  };




  return (
    <div className={`app-container ${theme}-theme`}>
      <h1>♟️ React Chess</h1>

      <div className="time-control">
        <label>
          Select time (minutes):{" "}
          <select
            value={timeControl / 60}
            onChange={(e) => setTimeControl(parseInt(e.target.value) * 60)}
            disabled={timerActive}
          >
            {[1, 3, 5, 10, 15].map((min) => (
              <option key={min} value={min}>
                {min}
              </option>
            ))}
          </select>
        </label>



        <button onClick={undoMove} disabled={!history.length}>Undo</button>
        <button onClick={downloadPGN} disabled={!history.length}>Export PGN</button>
        <button onClick={toggleTheme}>
          {theme === "light" ? "🌙 Dark Mode" : "🌞 Light Mode"}
        </button>

        <label style={{ marginLeft: "20px" }}>
          <input
            type="checkbox"
            checked={vsComputer}
            onChange={() => setVsComputer(!vsComputer)}
          />
          Play vs Computer
        </label>


        {vsComputer && (
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            style={{ marginLeft: "10px" }}
          >
            <option value="easy">🟢 Easy</option>
            <option value="medium">🟠 Medium</option>
            <option value="hard">🔴 Hard</option>
          </select>
        )}

      </div>

      <div className="board-timers-container">
        <div className="timer white-timer" style={{ backgroundColor: "#3B82F6", color: "white" }}>
          White: {formatTime(whiteTime)}
        </div>

        <div className="board-wrapper">


          <div className="board-and-files">
            <Chessboard
              position={position}
              onPieceDrop={onDrop}
              boardOrientation={playerColor === "w" ? "white" : "black"}
              showBoardNotation={true}
              boardWidth={400}  // 👈 This controls the actual size!
              customBoardStyle={{
                borderRadius: "8px",
                boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
                margin: "auto"
              }}
            />



            <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "20px" }}>
              <button onClick={() => newGame("w")}>New Game (White)</button>
              <button onClick={() => newGame("b")}>New Game (Black)</button>
            </div>

          </div>
        </div>

        <div className="timer black-timer" style={{ backgroundColor: "#111827", color: "white" }}>
          Black: {formatTime(blackTime)}
        </div>
      </div>

      <div className="move-history">
        <h3>📜 Move History</h3>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>White</th>
              <th>Black</th>
            </tr>
          </thead>
          <tbody>{renderHistory()}</tbody>
        </table>
      </div>
    </div>
  );
}
